import { ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';

// Angular Material
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatTabsModule } from '@angular/material/tabs';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatBadgeModule } from '@angular/material/badge';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';

// Google Places Autocomplete - using custom directive with @googlemaps/js-api-loader
import { Loader } from '@googlemaps/js-api-loader';
import { PlaceAutocompleteDirective } from './shared/directives/place-autocomplete.directive';

// Firebase
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';

import { AppRoutingModule } from './app-routing.module';
import { RouterModule } from '@angular/router';
import { AppComponent } from './app.component';
import { environment } from '../environments/environment';
import { LoginComponent } from './features/login/login.component';
import { OtpComponent } from './features/login/otp.component';
import { ForgotPasswordComponent } from './features/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './features/reset-password/reset-password.component';
import { NewRequestComponent } from './features/admin/new-request/new-request.component';
import { HistoryComponent } from './features/admin/history/history.component';
import { DataViewerComponent } from './features/data-viewer/data-viewer.component';
import { AddressBookComponent } from './features/address-book/address-book.component';
import { UsersComponent } from './features/admin/users/users.component';
import { CampaignsComponent } from './features/admin/campaigns/campaigns.component';
import { CampaignDialogComponent } from './features/admin/campaigns/campaign-dialog.component';
import { CampaignUrlDialogComponent } from './features/admin/campaigns/campaign-url-dialog.component';
import { UserAuditLogComponent } from './features/admin/users/user-audit-log.component';
import { FeatureFlagsComponent } from './features/admin/feature-flags/feature-flags.component';
import { AddEditUserDialogComponent } from './features/admin/users/add-edit-user-dialog.component';
import { IdDocumentPreviewDialogComponent } from './features/admin/users/id-document-preview-dialog.component';
import { DataRequestComponent } from './features/user/data-request/data-request.component';
import { SidebarComponent } from './layout/sidebar/sidebar.component';
import { ProgressDialogComponent } from './shared/components/progress-dialog/progress-dialog.component';
import { ConsentModalComponent } from './shared/components/consent-modal/consent-modal.component';
import { SessionTimeoutDialogComponent } from './shared/components/session-timeout-dialog/session-timeout-dialog.component';
import { SmallLoadingDialogComponent } from './shared/components/small-loading-dialog/small-loading-dialog.component';
import { StreetSearchComponent } from './features/admin/street-search/street-search.component';
import { StreetSummaryDialogComponent } from './features/admin/street-search/street-summary-dialog.component';
import { StreetInfoComponent } from './features/admin/street-search/street-info.component';
import { StreetSubjectProfileDialogComponent } from './features/admin/street-search/street-subject-profile-dialog.component';
import { MultiSearchComponent } from './features/admin/multi-search/multi-search.component';
import { MyAccountComponent, UpdateCardConfirmDialogComponent } from './features/my-account/my-account.component';
import { BillingPlanSelectorComponent } from './features/billing-plan-selector/billing-plan-selector.component';
import { TopupPackCardsComponent } from './shared/components/topup-pack-cards/topup-pack-cards.component';
import { RegisterComponent } from './features/register/register.component';
import { AuthExpiryInterceptor } from './core/auth/auth-expiry.interceptor';
import { AnalyticsEventsInterceptor } from './shared/interceptors/analytics-events.interceptor';
import { ErrorSupportCodeInterceptor } from './shared/interceptors/error-support-code.interceptor';
import { GlobalErrorHandlerService } from './shared/services/global-error-handler.service';
import { PlatformAdminComponent } from './features/platform-admin/platform-admin.component';
import { PlatformOrganizationsTableComponent } from './features/platform-admin/platform-organizations-table.component';
import { PlatformCompanyAdminsTableComponent } from './features/platform-admin/platform-company-admins-table.component';
import { CreatePlatformOrganizationDialogComponent } from './features/platform-admin/create-platform-organization-dialog.component';
import { EditPlatformOrganizationDialogComponent } from './features/platform-admin/edit-platform-organization-dialog.component';
import { PlatformStatsComponent } from './features/platform-admin/platform-stats.component';
import { PlatformOrganizationBillingComponent } from './features/platform-admin/platform-organization-billing.component';
import { TrustRequestComponent } from './features/admin/trust-request/trust-request.component';
import { TrustDataViewerComponent } from './features/trust-data-viewer/trust-data-viewer.component';
import { DigitalOnboardingListComponent } from './features/digital-onboarding/digital-onboarding-list.component';
import { DigitalOnboardingWizardComponent } from './features/digital-onboarding/digital-onboarding-wizard.component';
import { DigitalOnboardingResultsComponent } from './features/digital-onboarding/digital-onboarding-results.component';

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    OtpComponent,
    ForgotPasswordComponent,
    ResetPasswordComponent,
    NewRequestComponent,
    HistoryComponent,
    AddressBookComponent,
    DataViewerComponent,
    UsersComponent,
    UserAuditLogComponent,
    CampaignsComponent,
    CampaignDialogComponent,
    CampaignUrlDialogComponent,
    FeatureFlagsComponent,
    AddEditUserDialogComponent,
    IdDocumentPreviewDialogComponent,
    DataRequestComponent,
    SidebarComponent,
    ProgressDialogComponent,
    SmallLoadingDialogComponent,
    ConsentModalComponent,
    SessionTimeoutDialogComponent,
    StreetSearchComponent,
    StreetSummaryDialogComponent,
    StreetInfoComponent,
    StreetSubjectProfileDialogComponent,
    MultiSearchComponent,
    MyAccountComponent,
    UpdateCardConfirmDialogComponent,
    BillingPlanSelectorComponent,
    TopupPackCardsComponent,
    RegisterComponent,
    PlatformAdminComponent,
    PlatformOrganizationsTableComponent,
    PlatformCompanyAdminsTableComponent,
    CreatePlatformOrganizationDialogComponent,
    EditPlatformOrganizationDialogComponent,
    PlatformStatsComponent,
    PlatformOrganizationBillingComponent,
    TrustRequestComponent,
    TrustDataViewerComponent,
    DigitalOnboardingListComponent,
    DigitalOnboardingWizardComponent,
    DigitalOnboardingResultsComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    ReactiveFormsModule,
    FormsModule,
    MatToolbarModule,
    MatSidenavModule,
    MatListModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatRadioModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatSnackBarModule,
    MatMenuModule,
    MatTooltipModule,
    MatCardModule,
    MatChipsModule,
    MatTabsModule,
    MatExpansionModule,
    MatBadgeModule,
    MatSlideToggleModule,
    MatDatepickerModule,
    MatNativeDateModule,
    RouterModule,
    PlaceAutocompleteDirective
  ],
  providers: [
    provideAnimations(),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => getAuth()),
    {
      provide: Loader,
      useFactory: () => new Loader({
        apiKey: environment.googleMapsApiKey,
        libraries: ['places'],
        version: 'weekly'
      })
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthExpiryInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AnalyticsEventsInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorSupportCodeInterceptor,
      multi: true
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandlerService
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }

